/**
 * <pdp-buy-now> — "Buy Now" button that adds the current selection to the cart
 * and sends the customer straight to checkout, without opening the cart drawer.
 *
 * It reads the associated product form (by id) so it picks up the currently
 * selected variant, selling plan / one-time option, quantity and line-item
 * properties. It performs its own fetch to the cart-add route and then redirects
 * to /checkout — it does NOT dispatch the theme's CartAddEvent, so the drawer
 * stays closed.
 */
class PdpBuyNow extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-buy-now-button]');
    this.formId = this.dataset.formId;
    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  get form() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  cartAddUrl() {
    return (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  }

  checkoutUrl() {
    return (window.Routes && window.Routes.checkout) || '/checkout';
  }

  handleClick = async (event) => {
    event.preventDefault();
    const form = this.form;
    if (!form) return;

    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const formData = new FormData(form);

    // Mirror the theme's product-form.js: tag nurture subscriptions with a unique
    // bundle id so recurring line items group correctly; drop the marker for one-time.
    const isSubscription =
      formData.get('properties[_nurture_subscription]') === 'true' && formData.has('selling_plan');
    if (isSubscription) {
      formData.set('properties[_custom_bundle_id]', Date.now().toString());
    } else {
      formData.delete('selling_plan');
      formData.delete('properties[_nurture_subscription]');
      formData.delete('properties[_custom_bundle_id]');
    }
    formData.delete('purchase_option');

    this.setLoading(true);

    try {
      const response = await fetch(this.cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        this.setLoading(false);
        this.showError(data.description || data.message);
        return;
      }

      window.location.href = this.checkoutUrl();
    } catch (error) {
      this.setLoading(false);
      this.showError();
    }
  };

  setLoading(loading) {
    if (!this.button) return;
    this.button.disabled = loading;
    this.button.dataset.loading = loading ? 'true' : 'false';
  }

  showError(message) {
    const target = this.querySelector('[data-buy-now-error]');
    if (target) {
      target.textContent = message || 'Something went wrong. Please try again.';
      target.hidden = false;
    }
  }
}

if (!customElements.get('pdp-buy-now')) {
  customElements.define('pdp-buy-now', PdpBuyNow);
}
